import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Mail, CreditCard } from "lucide-react";
import { OwnerStats } from "@/components/app/OwnerStats";
import { Button } from "@/components/ui/button";
import { Checklist, isOnboardingComplete, STEPS, stepState } from "@/components/app/Checklist";
import { PageHeader } from "@/pages/app/AppLayout";
import { InstallApp } from "@/components/app/InstallApp";
import { EmployeeHome } from "@/components/app/EmployeeHome";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LinkedOverview, useLinks } from "@/components/app/LinkedBusiness";


const Stat = ({ icon: Icon, label, value, to, id }) => (
  <Link to={to} className="rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md" data-testid={`stat-${id}`}>
    <Icon className="h-5 w-5 text-[#00A85C]" />
    <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-mono-num mt-1 text-2xl font-bold" data-testid={`stat-${id}-value`}>{value}</p>
  </Link>
);

const LinkedSwitch = ({ view, setView }) => {
  const { business, membership, switchBusiness } = useAuth();
  const { linked } = useLinks(membership.role === "owner");
  if (linked.length < 2) return null;
  const opts = [["all", "All linked businesses"], ...linked.map((b) => [b.business_id, b.name])];
  const cur = view === "all" ? "all" : business.business_id;
  return (
    <div className="mb-5 flex flex-wrap gap-2" data-testid="linked-switch">{opts.map(([id, name]) => (
      <button key={id} onClick={() => { if (id === "all") setView("all"); else { setView("one"); if (id !== business.business_id) switchBusiness(id); } }} data-testid={`linked-switch-${id}`}
        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${cur === id ? "border-[#00C16B] bg-[#00C16B] text-white" : "border-slate-200 bg-white text-[#0B2D5B]"}`}>{name}</button>))}
    </div>
  );
};

const ManagerDashboard = () => {
  const { business, membership } = useAuth();
  const [view, setView] = useState("one");
  const [stats, setStats] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/workplaces"), api.get("/members"), api.get("/invitations")])
      .then(([w, m, i]) => setStats({ workplaces: w.data.length, members: m.data.filter((x) => x.status === "active").length, pending: i.data.filter((x) => x.status === "pending").length }))
      .catch(() => setStats({ workplaces: 0, members: 0, pending: 0 }));
  }, [business?.business_id]);
  if (!business) return null;
  const sub = business.subscription || {};
  const showChecklist = membership.role === "owner" && !isOnboardingComplete(business);
  return (
    <div data-testid="manager-dashboard">
      <PageHeader eyebrow="Dashboard" title={view === "all" ? "All linked businesses" : business.name} />
      <LinkedSwitch view={view} setView={setView} />
      {view === "all" ? <LinkedOverview /> : (
      <div className="grid gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-8">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            <Stat icon={MapPin} id="workplaces" label="Workplaces" value={stats?.workplaces ?? "…"} to="/app/geofences" />
            <Stat icon={Users} id="members" label="Team members" value={stats?.members ?? "…"} to="/app/employees" />
            <Stat icon={Mail} id="pending-invites" label="Pending invites" value={stats?.pending ?? "…"} to="/app/employees" />
            <Stat icon={CreditCard} id="plan" label={sub.status === "trialing" ? "Trial ends" : "Plan"} value={sub.status === "trialing" && sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString("en-AU") : sub.status === "trialing" ? "—" : "None"} to={membership.role === "owner" ? "/app/subscription" : "/app"} />
          </div>
          <OwnerStats />
        </div>
        {showChecklist && (
          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5 xl:col-span-4" data-testid="dashboard-checklist">
            <Checklist business={business} compact />
            <Link to="/app/onboarding"><Button className="mt-4 w-full rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid="continue-setup-btn">{STEPS.some((s) => !s.optional && stepState(business, s.key) !== "done") ? "Continue setup" : "Finish skipped steps"}</Button></Link>
          </div>
        )}
      </div>)}
    </div>
  );
};

const EmployeeDashboard = EmployeeHome;

export default function Dashboard() {
  const { membership } = useAuth();
  return membership.role === "employee" ? <EmployeeDashboard /> : <ManagerDashboard />;
}
